import React from "react";
import CustomDataTable from "src/components/CustomDataTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "src/components/ui/tabs";
import { Link, useSearchParams } from "react-router-dom";
import CustomSheet from "src/components/CustomSheet";
import SettlementDetailsModal from "src/components/modals/SettlementDetailsModal";
import { cn, getCustomizationValue, renderCurrency } from "src/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";
import { Button } from "src/components/ui/button";
import { SettingsIcon } from "src/svg";
import CustomDateRange from "src/components/CustomDateRange";
import { useQuery } from "@tanstack/react-query";
import { getCollectionSettlements, getOutstandingSettlements, getPendingSettlements, getSettlementData } from "src/api/settlements";
import { format } from "date-fns";
import withPermissionWrapper from "src/hooks/withPermissionWrapper";
import SettlementTransactions from "src/components/modals/SettlementTransactions";
import { useAuth } from "src/providers/AuthProvider";

const returnTitle = (status) =>
	status === "outstanding" ? "Outstanding Balance" : status === "collection" ? "Collection Settlements" : "Pending Settlements";

const Settlements = () => {
	const [searchParams, setSearchParams] = useSearchParams();

	const page = searchParams.get("page") || 1;
	const limit = searchParams.get("limit") || 10;
	const tab = searchParams.get("tab") ?? "pending";

	const updateSearchParams = (key, value) => {
		const newSearchParams = new URLSearchParams(searchParams);

		newSearchParams.set(key, value);

		setSearchParams(newSearchParams);
	};

	const { data } = useQuery({
		queryKey: ["settlements", page, limit],
		queryFn: () => getSettlementData({ page, limit }),
		suspense: true,
	});

	const settlement = data?.data;

	return (
		<div className="pageWrapper">
			<div className="pageTop">
				<h2 className="tw-text-[22px] tw-font-semibold">
					Settlements {">"} {returnTitle(tab)}
				</h2>
			</div>

			<div className="innerContent">
				<div className="tw-grid tw-gap-4 tw-mb-8 sm:tw-grid-cols-2 md:tw-grid-cols-[repeat(auto-fill,minmax(250px,1fr))]">
					<div className="tw-rounded-lg tw-px-6 tw-py-5 tw-bg-[#002366]">
						<p className="tw-text-white tw-font-medium">Collection Settlements</p>
						<p className="tw-text-[1.8rem] tw-font-semibold tw-text-white">{renderCurrency(settlement?.collection_settlement)}</p>
					</div>
					<div className="tw-rounded-lg tw-px-6 tw-py-5 tw-bg-[#333333]">
						<p className="tw-text-white tw-font-medium">Pending Settlements</p>
						<p className="tw-text-[1.8rem] tw-font-semibold tw-text-white">{renderCurrency(settlement?.pending_settlement)}</p>
					</div>
					<div className="tw-rounded-lg tw-px-6 tw-py-5 tw-bg-[#008080]">
						<p className="tw-text-white tw-font-medium">Paid Amount</p>
						<p className="tw-text-[1.8rem] tw-font-semibold tw-text-white">{renderCurrency(settlement?.paid_amount)}</p>
					</div>
					<div className="tw-rounded-lg tw-px-6 tw-py-5 tw-bg-[#D84A4A]">
						<p className="tw-text-white tw-font-medium">Outstanding Balance</p>
						<p className="tw-text-[1.8rem] tw-font-semibold tw-text-white">{renderCurrency(settlement?.outstanding_balance)}</p>
					</div>
				</div>
				<Tabs defaultValue="pending" value={tab} onValueChange={(tab) => updateSearchParams("tab", tab)}>
					<TabsList className="!tw-p-0 tw-flex-wrap !tw-justify-end tw-mb-5 tw-w-full">
						<TabsTrigger value="pending">Pending Settlements</TabsTrigger>
						<TabsTrigger value="collection">Collection Settlements</TabsTrigger>
						<TabsTrigger value="outstanding">Outstanding Balance</TabsTrigger>
						<Popover>
							<PopoverTrigger className="" asChild>
								<Button variant="secondary">
									<SettingsIcon />
								</Button>
							</PopoverTrigger>
							<PopoverContent className={cn("tw-p-2 tw-w-fit")}>
								<Filters />
							</PopoverContent>
						</Popover>
					</TabsList>
					<TabsContent value="pending">
						<Pending />
					</TabsContent>
					<TabsContent value="collection">
						<Collection />
					</TabsContent>
					<TabsContent value="outstanding">
						<Outstanding />
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
};

const Filters = () => {
	return <CustomDateRange />;
};

const Pending = () => {
	const { user } = useAuth();
	const [searchParams] = useSearchParams();
	const page = searchParams.get("page") || 1;
	const limit = searchParams.get("limit") || 10;

	const columns = [
		{ name: "S/N", selector: (_, index) => index + 1, width: "90px" },
		{ name: cn(getCustomizationValue(user, "customer_label", true), " ID"), selector: (row) => "10039294" },
		{ name: cn(getCustomizationValue(user, "customer_label", true), " Name"), selector: (row) => `${row.first_name} ${row.last_name}` },
		{ name: "Business Name", selector: (row) => row.business_name ?? "-" },
		{ name: "Mode of Payment", selector: (row) => row.payment_method, style: { textTransform: "capitalize" } },
		{ name: "Transaction Date", selector: (row) => format(row.due_date, "PPP") },
		{ name: "Amount Paid", selector: (row) => renderCurrency(row.amount) },
		{
			name: "Action",
			cell: (row) => (
				<CustomSheet
					title="Transaction Details"
					trigger={<span className="tw-text-primary tw-font-semibold hover:tw-underline">View Receipt</span>}
				>
					<SettlementDetailsModal settlement={row} />
				</CustomSheet>
			),
		},
	];

	const { data, isLoading } = useQuery({
		queryKey: ["settlements", "pending", page, limit],
		queryFn: () => getPendingSettlements({ page, limit }),
	});

	const settlements = data?.data?.data;
	const totalRow = data?.data?.total;

	return <CustomDataTable data={settlements} columns={columns} progressPending={isLoading} paginationTotalRows={totalRow} />;
};

const Outstanding = () => {
	const { user } = useAuth();
	const [searchParams] = useSearchParams();
	const page = searchParams.get("page") || 1;
	const limit = searchParams.get("limit") || 10;

	const columns = [
		{ name: "S/N", selector: (_, index) => index + 1, width: "80px" },
		{ name: cn(getCustomizationValue(user, "invoice_label", true), " ID"), selector: (row) => row.invoice_id },
		{
			name: cn(getCustomizationValue(user, "customer_label", true), " Name"),
			selector: (row) => `${row.first_name} ${row.last_name}`,
			grow: 1.5,
		},
		{ name: "Business Name", selector: (row) => row.business_name ?? "-" },
		{ name: "Mode of Payment", selector: (row) => row.payment_method, style: { textTransform: "capitalize" } },
		{ name: "Transaction Date", selector: (row) => format(row.due_date, "PPP") },
		{ name: "Amount Paid", selector: (row) => renderCurrency(row.amount_paid) },
		{ name: "Outstanding", selector: (row) => renderCurrency(row.amount_owed) },
		{
			name: "Action",
			cell: (row) => (
				<Link to={`/invoices/view/${row.uuid}`} className="tw-text-primary tw-font-semibold hover:tw-underline">
					View
				</Link>
			),
			grow: 0.7,
		},
	];

	const { data, isLoading } = useQuery({
		queryKey: ["settlements", "outstanding", page, limit],
		queryFn: () => getOutstandingSettlements({ page, limit }),
	});

	const settlements = data?.data?.data;
	const totalRow = data?.data?.total;

	return <CustomDataTable data={settlements} columns={columns} progressPending={isLoading} paginationTotalRows={totalRow} />;
};

const Collection = () => {
	const [searchParams] = useSearchParams();
	const page = searchParams.get("page") || 1;
	const limit = searchParams.get("limit") || 10;

	const columns = [
		{ name: "S/N", selector: (_, index) => index + 1, width: "80px" },
		{ name: "Title", selector: (row) => row.title },
		{ name: "Bank Name", selector: (row) => row.bank_name },
		{ name: "Bank Acct. Name", selector: (row) => row.account_name, grow: 2 },
		{ name: "Bank Acct No.", selector: (row) => row.account_number },
		{
			name: "Action",
			cell: (row) => (
				<CustomSheet
					title="Settlement Transactions"
					className="!tw-max-w-[45rem]"
					trigger={<span className="tw-text-primary tw-font-semibold hover:tw-underline">View Transactions</span>}
				>
					<SettlementTransactions uuid={row.uuid} />
				</CustomSheet>
			),
			grow: 1.2,
		},
	];

	const { data, isLoading } = useQuery({
		queryKey: ["settlements", "collection", page, limit],
		queryFn: () => getCollectionSettlements({ page, limit }),
	});

	const settlements = data?.data?.data;
	const totalRow = data?.data?.total;

	return <CustomDataTable data={settlements} columns={columns} progressPending={isLoading} paginationTotalRows={totalRow} />;
};

const Comp = withPermissionWrapper(Settlements, "view_settlements");
export default Comp;
